import { useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { Link } from 'react-router-dom';

const timeline = [
  {
    year: '2008',
    title: 'The Beginning',
    desc: 'Founded in Nan\'an City, Fujian Province, Yunxin started as a small workshop with 20 craftspeople and a singular vision: to make the world\'s most reliable tool bags.',
    image: 'https://readdy.ai/api/search-image?query=small%20workshop%20craftsmen%20sewing%20bags%20humble%20beginnings%20factory%20Fujian%20China%20vintage%20warm%20lighting&width=600&height=400&seq=bs1&orientation=landscape',
    align: 'left',
  },
  {
    year: '2012',
    title: 'Going Global',
    desc: 'Our first international export order arrived from a European distributor. This milestone validated our quality and set us on the path to becoming a global brand.',
    image: 'https://readdy.ai/api/search-image?query=international%20shipping%20containers%20port%20export%20global%20trade%20China%20professional%20photography&width=600&height=400&seq=bs2&orientation=landscape',
    align: 'right',
  },
  {
    year: '2016',
    title: 'Factory Expansion',
    desc: 'We expanded to a 15,000 sqm state-of-the-art facility, investing in modern machinery while preserving the handcraft traditions that define our quality.',
    image: 'https://readdy.ai/api/search-image?query=modern%20bag%20factory%20expansion%20new%20facility%20production%20line%20workers%20Fujian%20China&width=600&height=400&seq=bs3&orientation=landscape',
    align: 'left',
  },
  {
    year: '2019',
    title: 'ISO Certification',
    desc: 'Achieved ISO 9001:2015 certification — a formal recognition of the quality management systems we had been practicing since day one.',
    image: 'https://readdy.ai/api/search-image?query=quality%20certification%20ISO%20award%20ceremony%20professional%20business%20achievement&width=600&height=400&seq=bs4&orientation=landscape',
    align: 'right',
  },
  {
    year: '2026',
    title: 'The Future',
    desc: 'Today, Yunxin serves professionals in 50+ countries. We continue to innovate, launching our R&D lab to develop next-generation materials and smart storage solutions.',
    image: 'https://readdy.ai/api/search-image?query=modern%20research%20development%20lab%20innovation%20future%20technology%20bags%20materials%20testing&width=600&height=400&seq=bs5&orientation=landscape',
    align: 'left',
  },
];

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

const brandStorySchema = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  '@id': `${SITE_URL}/brand-story`,
  url: `${SITE_URL}/brand-story`,
  name: 'Brand Story | YUNXIN OUTDOOR',
  description: 'The story of Yunxin Outdoor — from a small workshop in Fujian to a global manufacturer of premium tool bags trusted by professionals in 50+ countries.',
  about: {
    '@type': 'Organization',
    name: 'Quanzhou Yunxin Outdoor Products Co., Ltd.',
    foundingDate: '2008',
    foundingLocation: {
      '@type': 'Place',
      name: "Nan'an City, Fujian Province, China",
    },
  },
};

export default function BrandStoryPage() {
  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'schema-brand-story';
    script.text = JSON.stringify(brandStorySchema);
    document.head.appendChild(script);
    document.title = 'Brand Story | YUNXIN OUTDOOR — Craft & Passion Since 2008';
    const desc = document.querySelector('meta[name="description"]');
    if (desc) desc.setAttribute('content', 'Discover the Yunxin Outdoor brand story — from a small workshop in Fujian to a global manufacturer of premium tool bags and organizer bags trusted in 50+ countries.');
    return () => {
      const el = document.getElementById('schema-brand-story');
      if (el) el.remove();
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-24 overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=artisan%20hands%20carefully%20stitching%20premium%20leather%20bag%20close%20up%20detail%20craftsmanship%20warm%20dramatic%20lighting%20workshop&width=1920&height=700&seq=bshero&orientation=landscape"
          alt="Brand Story"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-transparent" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10">
          <span className="text-amber-400 text-xs font-semibold tracking-widest uppercase mb-4 block">Brand Story</span>
          <h1 className="text-5xl md:text-7xl font-bold text-white leading-tight mb-6 max-w-2xl">
            A Story of<br />Craft &amp; Passion
          </h1>
          <p className="text-white/70 text-lg max-w-lg leading-relaxed">
            Every bag we make carries the story of our people, our values, and our relentless pursuit of excellence.
          </p>
        </div>
      </section>

      {/* Philosophy */}
      <section className="py-20 bg-stone-50">
        <div className="max-w-4xl mx-auto px-6 md:px-10 text-center">
          <span className="text-amber-600 text-xs font-semibold tracking-widest uppercase mb-4 block">Our Philosophy</span>
          <h2 className="text-4xl md:text-5xl font-bold text-stone-900 leading-tight mb-8">
            &ldquo;Quality is not an act,<br />it is a habit.&rdquo;
          </h2>
          <p className="text-stone-500 text-lg leading-relaxed">
            At Yunxin, we believe that the tools professionals carry say something about who they are. That&apos;s why we pour our hearts into every stitch, every zipper, every pocket — so that when you reach for your bag, it never lets you down.
          </p>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="text-center mb-16">
            <span className="text-amber-600 text-xs font-semibold tracking-widest uppercase mb-3 block">Our Journey</span>
            <h2 className="text-4xl md:text-5xl font-bold text-stone-900">The Yunxin Timeline</h2>
          </div>
          <div className="space-y-16">
            {timeline.map((item) => (
              <div
                key={item.year}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-10 items-center ${item.align === 'right' ? 'lg:flex-row-reverse' : ''}`}
              >
                <div className={item.align === 'right' ? 'lg:order-2' : ''}>
                  <div className="text-6xl font-bold text-amber-200 mb-2">{item.year}</div>
                  <h3 className="text-2xl font-bold text-stone-900 mb-4">{item.title}</h3>
                  <p className="text-stone-500 text-base leading-relaxed">{item.desc}</p>
                </div>
                <div className={`rounded-2xl overflow-hidden h-64 ${item.align === 'right' ? 'lg:order-1' : ''}`}>
                  <img src={item.image} alt={item.title} className="w-full h-full object-cover object-top" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 bg-stone-900">
        <div className="max-w-7xl mx-auto px-6 md:px-10 text-center">
          <span className="text-amber-400 text-xs font-semibold tracking-widest uppercase mb-4 block">Core Values</span>
          <h2 className="text-4xl font-bold text-white mb-12">What Drives Us Every Day</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { icon: 'ri-heart-line', title: 'Passion', desc: 'We love what we do. That passion translates into products that exceed expectations.' },
              { icon: 'ri-award-line', title: 'Excellence', desc: 'We never settle for good enough. Every product must meet our highest standards before it leaves our facility.' },
              { icon: 'ri-shake-hands-line', title: 'Integrity', desc: 'Honest business, fair pricing, and transparent communication — always.' },
            ].map((v) => (
              <div key={v.title} className="bg-stone-800 rounded-2xl p-8">
                <div className="w-12 h-12 flex items-center justify-center bg-amber-700 rounded-xl mb-5 mx-auto">
                  <i className={`${v.icon} text-white text-xl`} />
                </div>
                <h3 className="text-white font-semibold text-lg mb-3">{v.title}</h3>
                <p className="text-stone-400 text-sm leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-white text-center">
        <div className="max-w-2xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-stone-900 mb-4">Be Part of Our Story</h2>
          <p className="text-stone-500 text-base leading-relaxed mb-8">
            Join thousands of professionals worldwide who trust Yunxin bags every day.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 bg-stone-900 hover:bg-stone-700 text-white px-8 py-4 rounded-full text-sm font-semibold tracking-widest uppercase transition-all duration-300 whitespace-nowrap cursor-pointer"
          >
            Get In Touch
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-line" />
            </div>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
